import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Download,
  Upload,
  Database,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  HardDrive,
  FileArchive,
  Clock,
  Loader2,
  Cloud,
  CloudOff,
  ExternalLink,
  Trash2,
  Activity,
  Wrench,
  ImageDown
} from 'lucide-react';
import AutoBackupCard from './AutoBackupCard';
import { dateLocale } from '../lib/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Receipt filename backfill — admin-only one-shot maintenance.
 * Adds smart display names to receipts uploaded before the feature
 * existed. Strictly additive — never touches gridfs_id, file content,
 * or the expense↔receipt link.
 */
const ReceiptBackfillSection = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);

  const run = async (dry) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/expenses/backfill-receipt-names`, null, {
        params: { dry_run: dry },
      });
      if (dry) {
        setPreview(r.data);
        setApplied(null);
      } else {
        setApplied(r.data);
        setPreview(null);
        toast.success(
          `Renamed ${r.data.receipts_renamed} receipts across ${r.data.scanned_expenses} expenses.`
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const report = applied || preview;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => run(true)}
          disabled={loading}
          data-testid="backfill-dry-run-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
          Preview changes (dry run)
        </Button>
        <Button
          onClick={() => {
            if (!window.confirm(
              'Apply smart filenames to every receipt that doesn\'t have one yet?\n\n'
              + 'This only adds a display_name field — gridfs_id, file content '
              + 'and expense links are NEVER touched. Safe to re-run.'
            )) return;
            run(false);
          }}
          disabled={loading || !preview || preview.receipts_to_rename === 0}
          data-testid="backfill-apply-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          Apply backfill
        </Button>
      </div>
      {report && (
        <div className="rounded-md border bg-gray-50 p-3 text-sm">
          <div className="flex items-center gap-2 font-medium mb-2">
            {applied
              ? <CheckCircle2 className="h-4 w-4 text-green-600" />
              : <AlertCircle className="h-4 w-4 text-blue-600" />}
            {applied ? t('auto.set.backfillComplete') : t('auto.set.dryRunPreview')}
          </div>
          <ul className="space-y-0.5 text-gray-700">
            <li>{t('auto.set.scannedExpenses')} <strong>{report.scanned_expenses}</strong></li>
            <li>{t('auto.set.receiptsSeen')} <strong>{report.receipts_seen}</strong></li>
            <li>{t('auto.set.alreadyNamedSkipped')} <strong>{report.receipts_already_named}</strong></li>
            <li>
              {applied ? t('auto.set.receiptsRenamed') : t('auto.set.receiptsToRename')}:{' '}
              <strong>{applied ? report.receipts_renamed : report.receipts_to_rename}</strong>
            </li>
            {report.errors && report.errors.length > 0 && (
              <li className="text-orange-700">Errors: {report.errors.length}</li>
            )}
          </ul>
          {report.sample_renames && report.sample_renames.length > 0 && (
            <div className="mt-2">
              <div className="font-medium text-xs text-gray-500 mb-1">Sample (first {report.sample_renames.length}):</div>
              <div className="font-mono text-xs space-y-0.5 max-h-40 overflow-y-auto">
                {report.sample_renames.map((s, i) => (
                  <div key={i} className="truncate text-gray-600" title={`${s.from} → ${s.to}`}>
                    <span className="text-gray-400">{s.from || '(no name)'}</span>
                    <span className="mx-1">→</span>
                    <span className="text-gray-900">{s.to}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {report.errors && report.errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-orange-700">{t('auto.set.showErrors')}</summary>
              <pre className="mt-1 text-xs whitespace-pre-wrap bg-orange-50 p-2 rounded">{report.errors.join('\n')}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Format date to YYYY.MM.DD HH:MM
 */
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toISOString().replace('T', ' ').substring(0, 16).replace(/-/g, '.');
};

/**
 * Database Backup Tab Component
 */
const DatabaseBackupTab = () => {
  const { t } = useTranslation();
  const [backupInfo, setBackupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Storage audit state
  const [storageAudit, setStorageAudit] = useState(null);
  const [migrating, setMigrating] = useState(false);
  
  // Google Drive state
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [driveLoading, setDriveLoading] = useState(true);
  const [driveBackups, setDriveBackups] = useState([]);
  const [backingUpToDrive, setBackingUpToDrive] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [exportInfo, setExportInfo] = useState(null);
  const [fileMonths, setFileMonths] = useState([]);
  const [driveRedirectUri, setDriveRedirectUri] = useState('');
  const [savingUri, setSavingUri] = useState(false);
  const [restoringFromDrive, setRestoringFromDrive] = useState(null); // null | 'downloading' | 'restoring' | 'done'
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(null); // null | 'syncing' | 'restoring' | 'done'
  const [lostFiles, setLostFiles] = useState(null);
  const [backupHealth, setBackupHealth] = useState(null);

  const fetchBackupInfo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/backup/info`);
      setBackupInfo(response.data);
    } catch (err) {
      console.error('Error fetching backup info:', err);
      toast.error(t('auto.set.failedToLoadBackupInformation'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStorageAudit = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/backup/storage-audit`);
      setStorageAudit(response.data);
    } catch (err) {
      console.error('Error fetching storage audit:', err);
    }
  }, []);

  const handleMigrateFiles = async () => {
    setMigrating(true);
    try {
      const response = await axios.post(`${API}/backup/migrate-files`);
      const { total_migrated, migrated } = response.data;
      if (total_migrated > 0) {
        toast.success(`Migrated ${total_migrated} files to persistent storage (${migrated.receipts} receipts, ${migrated.attachments} attachments, ${migrated.avatars} avatars, ${migrated.logos} logos)`);
      } else {
        toast.info(t('auto.set.allAvailableFilesAreAlreadyIn'));
      }
      fetchStorageAudit();
      fetchBackupInfo();
    } catch (err) {
      console.error('Error migrating files:', err);
      toast.error(err.response?.data?.detail || 'Failed to migrate files');
    } finally {
      setMigrating(false);
    }
  };

  const [fixingDates, setFixingDates] = useState(false);
  const handleFixDateTypes = async () => {
    setFixingDates(true);
    try {
      const response = await axios.post(`${API}/backup/fix-date-types`);
      const { total_fields_fixed, collections_affected } = response.data;
      if (total_fields_fixed > 0) {
        toast.success(`Fixed ${total_fields_fixed} date fields across ${collections_affected} collections`);
      } else {
        toast.info(t('auto.set.allDateFieldsAreAlreadyCorrect'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fix date types');
    } finally {
      setFixingDates(false);
    }
  };

  // ---- Image storage reclaim ------------------------------------------
  // Images uploaded before the on-upload downscale existed are still at
  // full phone-camera size. Scan is read-only; Run rewrites each file
  // under the same GridFS id so every reference keeps resolving.
  const [reclaimScan, setReclaimScan] = useState(null);
  const [reclaimScanning, setReclaimScanning] = useState(false);
  const [reclaimRunning, setReclaimRunning] = useState(false);
  const [reclaimResult, setReclaimResult] = useState(null);
  const [reclaimConfirmOpen, setReclaimConfirmOpen] = useState(false);

  const handleReclaimScan = async () => {
    setReclaimScanning(true);
    setReclaimResult(null);
    try {
      const r = await axios.get(`${API}/backup/image-reclaim/scan`);
      setReclaimScan(r.data);
      if (r.data.candidates === 0) toast.info(t('auto.set.everyStoredImageIsAlreadyAt'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setReclaimScanning(false);
    }
  };

  const handleReclaimRun = async () => {
    setReclaimConfirmOpen(false);
    setReclaimRunning(true);
    try {
      const r = await axios.post(`${API}/backup/image-reclaim/run`);
      setReclaimResult(r.data);
      toast.success(`Recompressed ${r.data.recompressed} images — ${r.data.mb_reclaimed} MB reclaimed`);
      await handleReclaimScan();
      fetchStorageAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reclaim failed');
    } finally {
      setReclaimRunning(false);
    }
  };


  const fetchDriveStatus = useCallback(async () => {
    setDriveLoading(true);
    try {
      const response = await axios.get(`${API}/drive/status`);
      setDriveStatus(response.data);
      
      // Only fetch backups if fully connected (no errors)
      if (response.data.connected && !response.data.error) {
        try {
          const backupsResponse = await axios.get(`${API}/drive/backups`);
          setDriveBackups(backupsResponse.data.backups || []);
        } catch {
          // Backups fetch failed but connection is still valid
        }
      }
    } catch (err) {
      console.error('Error fetching drive status:', err);
      setDriveStatus({ connected: false });
    } finally {
      setDriveLoading(false);
    }
    // Fetch redirect URI
    try {
      const uriResp = await axios.get(`${API}/drive/redirect-uri`);
      setDriveRedirectUri(uriResp.data.redirect_uri || '');
    } catch {}
    // Fetch file sync status
    try {
      const syncResp = await axios.get(`${API}/drive/sync/status`);
      setSyncStatus(syncResp.data);
    } catch {}
    // Fetch backup health snapshot for the top-of-tab banner
    try {
      const healthResp = await axios.get(`${API}/drive/backup/health`);
      setBackupHealth(healthResp.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchBackupInfo();
    fetchDriveStatus();
    fetchStorageAudit();
    // Fetch lost files summary
    axios.get(`${API}/backup/lost-files`).then(r => setLostFiles(r.data)).catch(() => {});
    
    // Check for drive_connected query param
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('drive_connected') === 'true') {
      toast.success(t('auto.set.googleDriveConnectedSuccessfully'));
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      fetchDriveStatus();
    }
    if (urlParams.get('drive_error')) {
      toast.error(`Failed to connect Google Drive: ${urlParams.get('drive_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchBackupInfo, fetchDriveStatus]);

  // Load export info and monthly breakdown on mount
  useEffect(() => {
    const loadInfo = async () => {
      try {
        const [infoResp, monthsResp] = await Promise.all([
          axios.get(`${API}/backup/export/info`),
          axios.get(`${API}/backup/export/files/months`),
        ]);
        setExportInfo(infoResp.data);
        setFileMonths(monthsResp.data.months || []);
      } catch (e) { /* ignore */ }
    };
    loadInfo();
  }, []);

  const handleExportDatabase = () => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error(t('auto.set.pleaseLogInAgain')); return; }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/backup/export/collections?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
    toast.success(t('auto.set.databaseBackupDownloading'));
  };

  const handleExportFilesMonth = (year, month) => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error(t('auto.set.pleaseLogInAgain')); return; }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/backup/export/files/${year}/${month}?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
    const label = new Date(year, month - 1).toLocaleDateString('en', { year: 'numeric', month: 'long' });
    toast.success(`Downloading files for ${label}...`);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        toast.error(t('auto.set.pleaseSelectAZipBackupFile'));
        return;
      }
      setSelectedFile(file);
      setRestoreDialogOpen(true);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;
    
    setRestoring(true);
    setRestoreDialogOpen(false);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/backup/restore`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 600000 // 10 minute timeout
      });
      
      toast.success(t('auto.set.databaseRestoredSuccessfullyRefreshingData'));
      
      // Refresh backup info
      await fetchBackupInfo();
      
      // Suggest page refresh to load new data
      setTimeout(() => {
        if (window.confirm(t('auto.set.databaseRestoredRefreshThePageTo'))) {
          window.location.reload();
        }
      }, 1000);
      
    } catch (err) {
      console.error('Error restoring backup:', err);
      toast.error(err.response?.data?.detail || 'Failed to restore database backup');
    } finally {
      setRestoring(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const [restoringFiles, setRestoringFiles] = useState(false);
  const fileRestoreRef = useRef(null);

  const handleFileRestore = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) { toast.error(t('auto.set.pleaseSelectAZipFile')); return; }
    
    setRestoringFiles(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await axios.post(`${API}/backup/restore-files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      const d = resp.data;
      toast.success(`Files restored: ${d.gridfs_files_restored} GridFS + ${d.local_files_restored} local`);
      await fetchBackupInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore files');
    } finally {
      setRestoringFiles(false);
      if (fileRestoreRef.current) fileRestoreRef.current.value = '';
    }
  };

  // Google Drive handlers
  const handleConnectDrive = async () => {
    try {
      const response = await axios.get(`${API}/drive/connect`);
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (err) {
      console.error('Error connecting to Drive:', err);
      toast.error(err.response?.data?.detail || 'Failed to connect to Google Drive');
    }
  };

  const handleDisconnectDrive = async () => {
    try {
      await axios.delete(`${API}/drive/disconnect`);
      setDriveStatus({ connected: false });
      setDriveBackups([]);
      toast.success(t('auto.set.googleDriveDisconnected'));
      setDisconnectDialogOpen(false);
    } catch (err) {
      console.error('Error disconnecting Drive:', err);
      toast.error(err.response?.data?.detail || 'Failed to disconnect Google Drive');
    }
  };

  const handleBackupToDrive = async () => {
    setBackingUpToDrive(true);
    try {
      const response = await axios.post(`${API}/drive/backup`);
      toast.success(t('auto.set.backupUploadedToGoogleDriveSuccessfully'));
      // Refresh backups list
      fetchDriveStatus();
    } catch (err) {
      console.error('Error backing up to Drive:', err);
      toast.error(err.response?.data?.detail || 'Failed to backup to Google Drive');
    } finally {
      setBackingUpToDrive(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('auto.set.loadingBackupInformation')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="database-backup-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Database className="h-5 w-5 shrink-0" />
            <span className="truncate">Database Backup &amp; Restore</span>
          </h2>
          <p className="text-sm text-gray-500">
            {t('auto.set.createAndRestoreFullDatabaseBackups')}
          </p>
        </div>
        <Button variant="outline" className="shrink-0 self-start sm:self-auto" onClick={() => { fetchBackupInfo(); fetchDriveStatus(); fetchStorageAudit(); }} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('auto.set.refresh')}
        </Button>
      </div>

      {/* Backup Health Banner */}
      {backupHealth && (() => {
        const tools = backupHealth.mongo_tools || {};
        const lb = backupHealth.last_backup;
        const usage = backupHealth.drive_usage || {};
        const lbFailed = lb && lb.status && lb.status !== 'success';
        const bannerTone = lbFailed
          ? 'border-red-200 bg-red-50/40'
          : tools.available
            ? 'border-green-200 bg-green-50/30'
            : 'border-amber-200 bg-amber-50/40';
        const fmtDate = (d) => {
          if (!d) return '—';
          try { return new Date(d).toLocaleString(dateLocale()); } catch { return String(d); }
        };
        return (
          <Card className={bannerTone} data-testid="backup-health-banner">
            <CardContent className="py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Column 1: mongo-tools status */}
                <div className="flex items-start gap-3">
                  <Wrench className={`h-5 w-5 mt-0.5 shrink-0 ${tools.available ? 'text-green-600' : 'text-amber-600'}`} />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('auto.set.mongodbTools')}</div>
                    {tools.available ? (
                      <>
                        <div className="text-sm font-semibold text-gray-900">
                          Ready · v{tools.version || '?'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {tools.source === 'bootstrapped' ? t('auto.set.downloadedOnDemand') : t('auto.set.fromSystemImage')} · BSON backups
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm font-semibold text-amber-800">{t('auto.set.usingJsonFallback')}</div>
                        <div className="text-xs text-amber-700">
                          mongodump unavailable — restore-safe but slower
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Column 2: last backup */}
                <div className="flex items-start gap-3">
                  <Activity className={`h-5 w-5 mt-0.5 shrink-0 ${lbFailed ? 'text-red-600' : lb ? 'text-green-600' : 'text-gray-400'}`} />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('auto.set.lastBackup')}</div>
                    {lb ? (
                      <>
                        <div className="text-sm font-semibold text-gray-900 truncate" title={lb.file_name || ''}>
                          {fmtDate(lb.at)}
                        </div>
                        <div className="text-xs text-gray-600 flex items-center gap-1">
                          <span className="capitalize">{lb.type}</span>
                          <span>·</span>
                          <span className={lbFailed ? 'text-red-700 font-medium' : 'text-green-700'}>
                            {lb.status}
                          </span>
                        </div>
                        {lbFailed && lb.error && (
                          <div
                            className="text-xs text-red-700 mt-1 line-clamp-2"
                            title={lb.error}
                            data-testid="backup-health-last-error"
                          >
                            {lb.error}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-600">{t('auto.set.noBackupsYet')}</div>
                    )}
                  </div>
                </div>

                {/* Column 3: Drive usage */}
                <div className="flex items-start gap-3">
                  <HardDrive className="h-5 w-5 mt-0.5 shrink-0 text-blue-600" />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('auto.set.driveUsage')}</div>
                    {usage.connected ? (
                      <>
                        <div className="text-sm font-semibold text-gray-900">
                          {usage.backup_count} backup{usage.backup_count === 1 ? '' : 's'}
                        </div>
                        <div className="text-xs text-gray-600">
                          {usage.total_mb >= 1024
                            ? `${(usage.total_mb / 1024).toFixed(2)} GB total`
                            : `${usage.total_mb.toFixed(2)} MB total`}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-gray-600">{t('auto.set.driveNotConnected')}</div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Google Drive Card */}
      <Card className={driveStatus.connected ? "border-green-200 bg-green-50/30" : "border-gray-200"}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {driveStatus.connected ? (
              <Cloud className="h-5 w-5 text-green-600" />
            ) : (
              <CloudOff className="h-5 w-5 text-gray-400" />
            )}
            Google Drive Cloud Backup
          </CardTitle>
          <CardDescription>
            {driveStatus.connected 
              ? driveStatus.email 
                ? `Connected to ${driveStatus.email} • Backups saved to "${driveStatus.folder_name}" folder`
                : driveStatus.error ? t('auto.set.connectedAdditionalSetupRequired') : t('auto.set.connectedToGoogleDrive')
              : 'Connect your Google Drive to automatically backup to the cloud'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {driveLoading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('auto.set.checkingConnection')}
            </div>
          ) : driveStatus.connected ? (
            <div className="space-y-4">
              {/* Error banner if API not enabled or other issue */}
              {driveStatus.error && (
                <div className={`p-3 rounded-lg text-sm ${driveStatus.needs_api_enabled ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {driveStatus.needs_api_enabled ? (
                    <>
                      <strong>{t('auto.set.actionRequired')}</strong> Google Drive API is not enabled in your Google Cloud project.{' '}
                      <a href="https://console.developers.google.com/apis/api/drive.googleapis.com/overview?project=546342862409"
                         target="_blank" rel="noopener noreferrer" className="underline font-medium">
                        {t('auto.set.enableItHere')}
                      </a>, wait a minute, then click Refresh.
                    </>
                  ) : driveStatus.needs_reauth ? (
                    <>{t('auto.set.credentialsExpiredPleaseDisconnectAndReconnect')}</>
                  ) : (
                    <>{driveStatus.error}</>
                  )}
                </div>
              )}
              {/* Connected status */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <CheckCircle2 className={`h-5 w-5 ${driveStatus.error ? 'text-amber-500' : 'text-green-600'}`} />
                  <span className={`font-medium ${driveStatus.error ? 'text-amber-700' : 'text-green-700'}`}>
                    {driveStatus.error ? t('auto.set.connectedSetupIncomplete') : t('auto.set.connected')}
                  </span>
                  {driveStatus.email && <Badge variant="outline" className="text-xs truncate max-w-[220px]">{driveStatus.email}</Badge>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    onClick={handleBackupToDrive}
                    disabled={backingUpToDrive}
                    data-testid="backup-to-drive-btn"
                  >
                    {backingUpToDrive ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('auto.set.uploading')}
                      </>
                    ) : (
                      <>
                        <Cloud className="h-4 w-4 mr-2" />
                        {t('auto.set.backupToDriveNow')}
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setDisconnectDialogOpen(true)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                   data-testid="btn-disconnect">
                    {t('auto.set.disconnect')}
                  </Button>
                </div>
              </div>

              {/* Scheduled auto-backup settings — only when Drive is connected */}
              <AutoBackupCard
                driveConnected={driveStatus.connected && !driveStatus.error}
                onChange={fetchDriveStatus}
              />

              {/* Backups list */}
              {driveBackups.length > 0 && (
                <div className="mt-4">
                  <Label className="text-xs text-gray-500 uppercase tracking-wide">{t('auto.set.recentBackupsInDrive')}</Label>
                  <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                    {driveBackups.slice(0, 5).map((backup) => (
                      <div key={backup.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 bg-white rounded border">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileArchive className="h-4 w-4 text-gray-400 shrink-0" />
                          <span className="text-sm font-medium truncate" title={backup.name}>{backup.name}</span>
                          <Badge variant="secondary" className="text-xs shrink-0">{backup.size_display}</Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 shrink-0">
                          <span className="text-xs text-gray-500">{formatDateTime(backup.created_at)}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            disabled={!!restoringFromDrive}
                            onClick={async () => {
                              const choice = window.confirm(`Restore from "${backup.name}"?\n\nThis will merge the backup with existing data.`);
                              if (!choice) return;
                              setRestoringFromDrive('downloading');
                              try {
                                setRestoringFromDrive('restoring');
                                const resp = await axios.post(`${API}/drive/restore/${backup.id}`, null, { timeout: 600000 });
                                const d = resp.data;
                                setRestoringFromDrive('done');
                                toast.success(`Restore complete! DB: ${d.db_restored ? 'restored' : 'skipped'}, Files: ${d.files_restored} restored${d.errors?.length ? `, ${d.errors.length} warning(s)` : ''}`);
                                setTimeout(() => setRestoringFromDrive(null), 5000);
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Restore failed');
                                setRestoringFromDrive(null);
                              }
                            }}
                            data-testid={`restore-drive-${backup.id}`}
                          >
                            {t('auto.set.restore')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                            disabled={!!restoringFromDrive}
                            onClick={async () => {
                              if (!window.confirm(`CLEAN RESTORE from "${backup.name}"?\n\nThis will DELETE ALL current data (database + uploaded files) and replace with the backup contents.\n\nThis cannot be undone.`)) return;
                              if (!window.confirm(`Are you absolutely sure? All current data will be permanently lost.`)) return;
                              setRestoringFromDrive('downloading');
                              try {
                                setRestoringFromDrive('restoring');
                                const resp = await axios.post(`${API}/drive/restore/${backup.id}?wipe=true`, null, { timeout: 600000 });
                                const d = resp.data;
                                setRestoringFromDrive('done');
                                toast.success(`Clean restore complete! DB: ${d.db_restored ? 'restored' : 'skipped'}, Files: ${d.files_restored} restored`);
                                setTimeout(() => setRestoringFromDrive(null), 5000);
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Restore failed');
                                setRestoringFromDrive(null);
                              }
                            }}
                            data-testid={`wipe-restore-drive-${backup.id}`}
                          >
                            {t('auto.set.cleanRestore')}
                          </Button>
                          {backup.web_link && (
                            <a 
                              href={backup.web_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            disabled={!!restoringFromDrive}
                            title={t('auto.set.deleteBackupFromDrive')}
                            onClick={async () => {
                              if (!window.confirm(`Delete "${backup.name}" from Google Drive?\n\nThis permanently removes the backup ZIP from your Drive. This cannot be undone.`)) return;
                              try {
                                await axios.delete(`${API}/drive/backup/${backup.id}`);
                                toast.success(`Deleted ${backup.name}`);
                                // Refresh the backup list
                                try {
                                  const r = await axios.get(`${API}/drive/backups`);
                                  setDriveBackups(r.data.backups || []);
                                } catch (_) {}
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Failed to delete backup');
                              }
                            }}
                            data-testid={`delete-drive-backup-${backup.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {driveBackups.length > 5 && (
                    <p className="text-xs text-gray-500 mt-2">
                      +{driveBackups.length - 5} more backups in Drive
                    </p>
                  )}
                </div>
              )}

              {/* Restore progress indicator */}
              {restoringFromDrive && (
                <div className={`mt-4 p-4 rounded-lg border flex items-center gap-3 ${restoringFromDrive === 'done' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                  {restoringFromDrive === 'done' ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0" />
                  )}
                  <div>
                    <p className={`font-medium text-sm ${restoringFromDrive === 'done' ? 'text-green-800' : 'text-blue-800'}`}>
                      {restoringFromDrive === 'downloading' && 'Downloading backup from Google Drive...'}
                      {restoringFromDrive === 'restoring' && 'Restoring database and files — this may take several minutes for large backups...'}
                      {restoringFromDrive === 'done' && 'Restore completed successfully!'}
                    </p>
                    {restoringFromDrive !== 'done' && (
                      <p className="text-xs text-blue-600 mt-1">{t('auto.set.pleaseDoNotCloseOrNavigate')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <Cloud className="h-4 w-4 inline mr-2" />
                Connect your personal Google Drive to store backups securely in the cloud.
                Backups will be saved to a folder called "Expertfix_Backups".
              </div>
              <Button onClick={handleConnectDrive} data-testid="connect-drive-btn">
                <Cloud className="h-4 w-4 mr-2" />
                {t('auto.set.connectGoogleDrive')}
              </Button>
            </div>
          )}
          {/* Redirect URI Configuration */}
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs font-medium text-gray-500 whitespace-nowrap">{t('auto.set.oauthRedirectUri')}</label>
              <Input
                value={driveRedirectUri}
                onChange={(e) => setDriveRedirectUri(e.target.value)}
                className="h-7 text-xs font-mono flex-1 min-w-[200px]"
                placeholder="https://your-domain.com/api/oauth/drive/callback"
                data-testid="drive-redirect-uri"
              />
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs whitespace-nowrap"
                disabled={savingUri}
                onClick={async () => {
                  setSavingUri(true);
                  try {
                    await axios.put(`${API}/drive/redirect-uri`, { redirect_uri: driveRedirectUri });
                    toast.success(t('auto.set.redirectUriSaved'));
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
                  finally { setSavingUri(false); }
                }}
                data-testid="save-redirect-uri-btn"
              >
                {savingUri ? t('auto.set.saving') : t('auto.set.save')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs whitespace-nowrap text-blue-600"
                onClick={async () => {
                  const prodUri = 'https://pm.expertfix.eu/api/oauth/drive/callback';
                  setDriveRedirectUri(prodUri);
                  setSavingUri(true);
                  try {
                    await axios.put(`${API}/drive/redirect-uri`, { redirect_uri: prodUri });
                    toast.success(t('auto.set.setToProductionUriAndSaved'));
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
                  finally { setSavingUri(false); }
                }}
                data-testid="set-production-uri-btn"
              >
                {t('auto.set.setProduction')}
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Must match the URI in your Google Cloud Console. Click "Set Production" after deploying to pm.expertfix.eu.</p>
          </div>
        </CardContent>
      </Card>

      {/* File Sync to Drive Card — only when Drive is connected without API errors */}
      {driveStatus.connected && !driveStatus.error && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              {t('auto.set.fileSyncToGoogleDrive')}
            </CardTitle>
            <CardDescription>
              Mirror all uploaded files (receipts, attachments, documents, photos) to Google Drive.
              Files are organized in folders matching the app structure.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Sync stats */}
            {syncStatus && (
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <span>{syncStatus.synced} synced</span>
                </div>
                {syncStatus.pending > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-amber-500" />
                    <span>{syncStatus.pending} pending</span>
                  </div>
                )}
                {syncStatus.errors > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-red-500" />
                    <span>{syncStatus.errors} failed</span>
                  </div>
                )}
                {syncStatus.last_run && (
                  <span className="text-xs text-gray-400">Last sync: {new Date(syncStatus.last_run).toLocaleString(dateLocale())}</span>
                )}
              </div>
            )}

            {/* Background sync progress indicator */}
            {(syncStatus?.running || syncing === 'syncing' || syncing === 'restoring') && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                <Loader2 className="h-4 w-4 text-blue-600 animate-spin flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    {syncing === 'restoring' ? t('auto.set.restoringFilesFromGoogleDriveTo') : t('auto.set.syncingFilesToGoogleDrive')}
                  </p>
                  <p className="text-xs text-blue-600">{t('auto.set.runningInBackgroundYouCanNavigate')}</p>
                </div>
              </div>
            )}
            {syncStatus?.last_result && !syncStatus?.running && syncing !== 'syncing' && syncing !== 'restoring' && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-800">
                  Last result: {syncStatus.last_result.synced ?? syncStatus.last_result.restored ?? 0} synced, {syncStatus.last_result.skipped ?? 0} skipped, {syncStatus.last_result.failed ?? 0} failed
                  {syncStatus.last_result.error && <span className="text-red-600"> — Error: {syncStatus.last_result.error}</span>}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!!syncing || syncStatus?.running}
                onClick={async () => {
                  setSyncing('syncing');
                  try {
                    await axios.post(`${API}/drive/sync/start`);
                    toast.info(t('auto.set.fileSyncStartedInBackground'));
                    // Poll for completion
                    const poll = setInterval(async () => {
                      try {
                        const statusResp = await axios.get(`${API}/drive/sync/status`);
                        setSyncStatus(statusResp.data);
                        if (!statusResp.data.running) {
                          clearInterval(poll);
                          setSyncing(null);
                          const lr = statusResp.data.last_result;
                          if (lr) toast.success(`Sync complete: ${lr.synced || 0} synced, ${lr.skipped || 0} skipped, ${lr.failed || 0} failed`);
                        }
                      } catch {}
                    }, 5000);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Sync failed to start');
                    setSyncing(null);
                  }
                }}
                data-testid="sync-files-to-drive-btn"
              >
                <Upload className="h-4 w-4 mr-1" />
                {t('auto.set.syncFilesToDrive')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!!syncing || syncStatus?.running}
                onClick={async () => {
                  if (!window.confirm(t('auto.set.forceReSyncAllFilesTo'))) return;
                  setSyncing('force-syncing');
                  try {
                    await axios.post(`${API}/drive/sync/start?force=true`);
                    toast.info(t('auto.set.forceReSyncStartedInBackground'));
                    const poll = setInterval(async () => {
                      try {
                        const statusResp = await axios.get(`${API}/drive/sync/status`);
                        setSyncStatus(statusResp.data);
                        if (!statusResp.data.running) {
                          clearInterval(poll);
                          setSyncing(null);
                          const lr = statusResp.data.last_result;
                          if (lr) toast.success(`Force re-sync complete: ${lr.synced || 0} uploaded, ${lr.skipped || 0} skipped, ${lr.failed || 0} failed`);
                        }
                      } catch {}
                    }, 5000);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Force re-sync failed to start');
                    setSyncing(null);
                  }
                }}
                data-testid="force-resync-files-to-drive-btn"
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                <Upload className="h-4 w-4 mr-1" />
                {t('auto.set.forceReSyncAll')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!!syncing || syncStatus?.running}
                onClick={async () => {
                  if (!window.confirm(t('auto.set.restoreAllFilesFromGoogleDrive'))) return;
                  setSyncing('restoring');
                  try {
                    await axios.post(`${API}/drive/sync/restore-files`);
                    toast.info(t('auto.set.fileRestoreStartedInBackground'));
                    const poll = setInterval(async () => {
                      try {
                        const statusResp = await axios.get(`${API}/drive/sync/status`);
                        setSyncStatus(statusResp.data);
                        if (!statusResp.data.running) {
                          clearInterval(poll);
                          setSyncing(null);
                          const lr = statusResp.data.last_result;
                          if (lr) toast.success(`Restore complete: ${lr.restored || 0} restored, ${lr.skipped || 0} skipped, ${lr.failed || 0} failed`);
                        }
                      } catch {}
                    }, 5000);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Restore failed to start');
                    setSyncing(null);
                  }
                }}
                data-testid="restore-files-from-drive-btn"
              >
                <Download className="h-4 w-4 mr-1" />
                {t('auto.set.restoreFilesFromDrive')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lost Files Card — orphaned references from pre-GridFS era */}
      {lostFiles && lostFiles.total > 0 && (
        <Card className="border-orange-200 bg-orange-50/30" data-testid="lost-files-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Lost Files — Re-upload Needed
            </CardTitle>
            <CardDescription>
              {t('auto.set.theseFilesWereReferencedInRecords')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-white rounded-lg border">
                <div className="text-xs text-gray-500">{t('auto.set.expenseReceipts')}</div>
                <div className="text-xl font-semibold text-orange-600">{lostFiles.expense_receipts}</div>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="text-xs text-gray-500">{t('auto.set.fileAttachments')}</div>
                <div className="text-xl font-semibold text-orange-600">{lostFiles.file_attachments}</div>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="text-xs text-gray-500">{t('auto.set.employeeAvatars')}</div>
                <div className="text-xl font-semibold text-orange-600">{lostFiles.employee_avatars}</div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-orange-300">
                <div className="text-xs text-gray-500">{t('auto.set.totalLost')}</div>
                <div className="text-xl font-semibold text-orange-700">{lostFiles.total}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 items-center pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const resp = await axios.get(`${API}/backup/lost-files/export`, { responseType: 'blob' });
                    const url = URL.createObjectURL(resp.data);
                    const a = document.createElement('a');
                    a.href = url;
                    const match = (resp.headers['content-disposition'] || '').match(/filename="(.+)"/);
                    a.download = match ? match[1] : `lost_files_${new Date().toISOString().slice(0,10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success(t('auto.set.lostFilesCsvDownloaded'));
                  } catch (e) {
                    toast.error(t('auto.set.failedToDownloadList'));
                  }
                }}
                data-testid="export-lost-files-btn"
                className="border-orange-400 text-orange-700 hover:bg-orange-100"
              >
                <Download className="h-4 w-4 mr-1" />
                {t('auto.set.downloadLostFilesListCsv')}
              </Button>
              <span className="text-xs text-gray-500">{t('auto.set.opensInExcelGoogleSheetsEach')}</span>
            </div>
            <div className="text-xs text-gray-600 pt-1 border-t border-orange-100">
              <strong>{t('auto.set.toReUploadAnExpenseReceipt')}</strong> open the expense in the Budget tab and click the orange upload icon. Each re-uploaded file is stored in GridFS and auto-mirrored to Google Drive, so it can never be lost to storage issues again.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Storage Health Card */}
      {storageAudit && (
        <Card className={
          (storageAudit.receipts?.lost > 0 || storageAudit.attachments?.lost > 0) 
            ? "border-red-200 bg-red-50/30" 
            : (storageAudit.receipts?.on_disk_only > 0 || storageAudit.attachments?.on_disk_only > 0)
              ? "border-yellow-200 bg-yellow-50/30"
              : "border-green-200 bg-green-50/30"
        }>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-blue-500" />
              {t('auto.set.fileStorageHealth')}
            </CardTitle>
            <CardDescription>
              {t('auto.set.filesInGridfsPersistentSurviveDeployments')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {['receipts', 'attachments', 'avatars', 'customer_logos'].map((category) => {
                const data = storageAudit[category] || {};
                const label = category === 'customer_logos' ? 'Logos' : category.charAt(0).toUpperCase() + category.slice(1);
                return (
                  <div key={category} className="p-3 bg-white rounded-lg border">
                    <div className="text-sm font-medium mb-2">{label}</div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500">{t('auto.set.total')}</span>
                        <span className="font-medium">{data.total || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-600">{t('auto.set.inGridfs')}</span>
                        <span className="font-medium text-green-600">{data.in_gridfs || 0}</span>
                      </div>
                      {data.on_disk_only > 0 && (
                        <div className="flex justify-between">
                          <span className="text-yellow-600">{t('auto.set.diskOnly')}</span>
                          <span className="font-medium text-yellow-600">{data.on_disk_only}</span>
                        </div>
                      )}
                      {data.lost > 0 && (
                        <div className="flex justify-between">
                          <span className="text-red-600">{t('auto.set.lost')}</span>
                          <span className="font-medium text-red-600">{data.lost}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-white rounded-lg border">
              <div className="min-w-0">
                <div className="text-sm font-medium">
                  GridFS Persistent Storage: {storageAudit.gridfs_total_files} files ({storageAudit.gridfs_total_size_mb} MB)
                </div>
                {(storageAudit.receipts?.on_disk_only > 0 || storageAudit.attachments?.on_disk_only > 0) && (
                  <div className="text-xs text-yellow-600 mt-1">
                    {t('auto.set.someFilesAreOnlyOnDisk')}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
              <Button 
                onClick={handleMigrateFiles}
                disabled={migrating}
                variant={(storageAudit.receipts?.on_disk_only > 0 || storageAudit.attachments?.on_disk_only > 0) ? "default" : "outline"}
                size="sm"
                data-testid="migrate-files-btn"
              >
                {migrating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('auto.set.migrating')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t('auto.set.migrateToGridfs')}
                  </>
                )}
              </Button>
              <Button 
                onClick={handleFixDateTypes}
                disabled={fixingDates}
                variant="outline"
                size="sm"
                data-testid="fix-dates-btn"
              >
                {fixingDates ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('auto.set.fixing')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t('auto.set.fixDateTypes')}
                  </>
                )}
              </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image Storage Reclaim */}
      <Card data-testid="image-reclaim-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ImageDown className="h-5 w-5 text-blue-500" />
            {t('auto.set.reclaimImageStorage')}
          </CardTitle>
          <CardDescription>
            New uploads are already downscaled to print size. Photos stored before that
            was in place are still at full camera size. Scanning changes nothing;
            recompressing rewrites each file under the same id, so every asset, report
            and PDF keeps working.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {reclaimScan && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-gray-700" data-testid="reclaim-bucket-mb">{reclaimScan.bucket_total_mb} MB</div>
                <div className="text-xs text-gray-500">Stored now ({reclaimScan.bucket_total_files} files)</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-amber-600" data-testid="reclaim-candidates">{reclaimScan.candidates}</div>
                <div className="text-xs text-gray-500">Oversized images ({reclaimScan.candidates_mb} MB)</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600" data-testid="reclaim-saving">~{reclaimScan.estimated_saving_mb} MB</div>
                <div className="text-xs text-gray-500">{t('auto.set.estimatedSaving')}</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{reclaimScan.max_dimension}px</div>
                <div className="text-xs text-gray-500">Target size · quality {reclaimScan.jpeg_quality}</div>
              </div>
            </div>
          )}

          {reclaimScan?.largest?.length > 0 && (
            <div className="rounded-lg border divide-y max-h-56 overflow-y-auto" data-testid="reclaim-file-list">
              {reclaimScan.largest.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs">
                  <span className="truncate text-gray-700">{f.filename}</span>
                  <span className="shrink-0 font-medium text-amber-600">{f.size_kb} KB</span>
                </div>
              ))}
            </div>
          )}

          {reclaimResult && (
            <div className="rounded-lg border border-green-200 bg-green-50/50 p-3 text-sm" data-testid="reclaim-result">
              Recompressed <strong>{reclaimResult.recompressed}</strong> of {reclaimResult.examined} images:
              {' '}{reclaimResult.mb_before} MB → {reclaimResult.mb_after} MB
              {' '}(<strong>{reclaimResult.mb_reclaimed} MB reclaimed</strong>).
              {reclaimResult.skipped_no_gain > 0 && ` ${reclaimResult.skipped_no_gain} already small enough.`}
              {reclaimResult.failed > 0 && ` ${reclaimResult.failed} could not be read and were left untouched.`}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleReclaimScan} disabled={reclaimScanning || reclaimRunning} variant="outline" size="sm" data-testid="reclaim-scan-btn">
              {reclaimScanning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('auto.set.scanning')}</> : <><Activity className="h-4 w-4 mr-2" />{t('auto.set.scanImages')}</>}
            </Button>
            <Button
              onClick={() => setReclaimConfirmOpen(true)}
              disabled={reclaimRunning || reclaimScanning || !reclaimScan?.candidates}
              size="sm"
              data-testid="reclaim-run-btn"
            >
              {reclaimRunning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('auto.set.recompressing')}</> : <><ImageDown className="h-4 w-4 mr-2" />{t('auto.set.recompressNow')}</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={reclaimConfirmOpen} onOpenChange={setReclaimConfirmOpen}>
        <AlertDialogContent data-testid="reclaim-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Recompress {reclaimScan?.candidates} images?</AlertDialogTitle>
            <AlertDialogDescription>
              Each one is resized to at most {reclaimScan?.max_dimension}px and re-saved at
              quality {reclaimScan?.jpeg_quality} — still good enough to print on A4. The original
              file id is kept, so nothing in the app loses its photo, and your Google Drive
              mirror still holds the untouched originals. This cannot be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reclaim-cancel-btn">{t('auto.set.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleReclaimRun} data-testid="reclaim-confirm-btn">
              Recompress {reclaimScan?.candidates} images
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Database Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-blue-500" />
            {t('auto.set.databaseInformation')}
          </CardTitle>
          <CardDescription>
            Current database: <strong>{backupInfo?.database_name}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{backupInfo?.total_collections || 0}</div>
              <div className="text-xs text-gray-500">{t('auto.set.collections')}</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{backupInfo?.total_documents?.toLocaleString() || 0}</div>
              <div className="text-xs text-gray-500">{t('auto.set.totalDocuments')}</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">{backupInfo?.files?.total_files || 0}</div>
              <div className="text-xs text-gray-500">{t('auto.set.uploadedFiles')}</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-600">{backupInfo?.files?.total_size_display || '0 MB'}</div>
              <div className="text-xs text-gray-500">{t('auto.set.filesSize')}</div>
            </div>
          </div>
          
          {/* Last Restore Info */}
          <div className="p-3 bg-gray-50 rounded-lg mb-4">
            <div className="text-xs text-gray-500 mb-1">{t('auto.set.lastRestore')}</div>
            {backupInfo?.last_restore?.date ? (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-sm">{formatDateTime(backupInfo.last_restore.date)}</span>
                <span className="text-xs text-gray-400">by {backupInfo.last_restore.by}</span>
                {backupInfo.last_restore.files_restored > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {backupInfo.last_restore.files_restored} files
                  </Badge>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-400">{t('auto.set.noRestoreHistory')}</div>
            )}
          </div>

          {/* Files breakdown */}
          {backupInfo?.files?.total_files > 0 && (
            <div className="mb-4">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">{t('auto.set.uploadedFilesByType')}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(backupInfo?.files?.by_folder || {}).map(([folder, info]) => (
                  <Badge key={folder} variant="outline" className="text-xs">
                    {folder}: {info.count} ({(info.size_bytes / 1024 / 1024).toFixed(1)}MB)
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Collections breakdown */}
          <div className="mt-4">
            <Label className="text-xs text-gray-500 uppercase tracking-wide">{t('auto.set.databaseCollections')}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {backupInfo?.collections?.slice(0, 15).map((coll) => (
                <Badge key={coll.name} variant="secondary" className="text-xs">
                  {coll.name}: {coll.document_count}
                </Badge>
              ))}
              {backupInfo?.collections?.length > 15 && (
                <Badge variant="outline" className="text-xs">
                  +{backupInfo.collections.length - 15} more
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Export Card */}
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-500" />
              {t('auto.set.downloadBackup')}
            </CardTitle>
            <CardDescription>
              {t('auto.set.downloadDatabaseAndFileBackupsFiles')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <CheckCircle2 className="h-4 w-4 inline mr-2" />
                <strong>{t('auto.set.total')}</strong>{' '}
                {exportInfo ? `${exportInfo.collections_count} collections, ${exportInfo.total_files} files (${exportInfo.total_files_size_mb} MB)` : 'Loading...'}
              </div>

              {/* Database backup — single button */}
              <Button
                onClick={handleExportDatabase}
                className="w-full"
                data-testid="export-database-btn"
              >
                <Database className="h-4 w-4 mr-2" />
                {t('auto.set.downloadDatabaseBackup')}
                {exportInfo && <span className="ml-1 text-xs opacity-70">({exportInfo.collections_count} collections)</span>}
              </Button>

              {/* Monthly file downloads */}
              {fileMonths.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <FileArchive className="h-3.5 w-3.5" />
                    {t('auto.set.fileBackupsByMonth')}
                  </div>
                  <div className="divide-y max-h-64 overflow-y-auto">
                    {fileMonths.map((m) => {
                      const label = m.year_month === 'unknown' ? 'Unknown date'
                        : new Date(m.year, m.month - 1).toLocaleDateString('en', { year: 'numeric', month: 'long' });
                      return (
                        <div key={m.year_month} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50" data-testid={`month-row-${m.year_month}`}>
                          <div>
                            <span className="text-sm font-medium">{label}</span>
                            <span className="ml-2 text-xs text-gray-500">{m.count} files, {m.size_mb} MB</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExportFilesMonth(m.year, m.month)}
                            disabled={!m.downloadable}
                            data-testid={`download-month-${m.year_month}`}
                          >
                            <Download className="h-3.5 w-3.5 mr-1" />
                            {m.downloadable ? t('auto.set.download') : t('auto.set.tooLarge')}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {fileMonths.length === 0 && (
                <div className="text-sm text-gray-400 text-center py-2">{t('auto.set.noUploadedFilesFound')}</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Restore Card */}
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="h-5 w-5 text-orange-500" />
              {t('auto.set.restoreBackup')}
            </CardTitle>
            <CardDescription>
              {t('auto.set.restoreInTwoSteps1Database')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-orange-50 rounded-lg text-sm text-orange-700">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                <strong>{t('auto.set.warning')}</strong> Database restore overwrites all current data. File restore adds missing files without overwriting.
              </div>

              {/* Step 1: Database restore */}
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{t('auto.set.step1RestoreDatabase')}</div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="restore-file-input"
                />
                <Button 
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={restoring || restoringFiles}
                  className="w-full border-orange-300 hover:bg-orange-50"
                  data-testid="restore-backup-btn"
                >
                  {restoring ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('auto.set.restoringDatabase')}
                    </>
                  ) : (
                    <>
                      <Database className="h-4 w-4 mr-2" />
                      {t('auto.set.selectDatabaseBackupZip')}
                    </>
                  )}
                </Button>
              </div>

              {/* Step 2: File restore */}
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{t('auto.set.step2RestoreFilesRepeatFor')}</div>
                <input
                  ref={fileRestoreRef}
                  type="file"
                  accept=".zip"
                  onChange={handleFileRestore}
                  className="hidden"
                  id="restore-files-input"
                />
                <Button 
                  variant="outline"
                  onClick={() => fileRestoreRef.current?.click()}
                  disabled={restoring || restoringFiles}
                  className="w-full border-blue-300 hover:bg-blue-50"
                  data-testid="restore-files-btn"
                >
                  {restoringFiles ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('auto.set.restoringFiles')}
                    </>
                  ) : (
                    <>
                      <FileArchive className="h-4 w-4 mr-2" />
                      {t('auto.set.selectMonthlyFileBackupZip')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Receipt filename backfill (one-shot maintenance) */}
      <Card className="border-blue-200" data-testid="receipt-backfill-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-600" />
            {t('auto.set.receiptFilenameMaintenance')}
          </CardTitle>
          <CardDescription>
            Add smart display names (<code className="text-xs">{t('auto.set.yyyyMmDdNameProject')}</code>)
            to receipts uploaded before this feature existed. Safe: only
            adds a new <code className="text-xs">display_name</code> field per
            receipt; nothing else is touched.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ReceiptBackfillSection />
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          <h4 className="font-medium mb-2">{t('auto.set.backupTips')}</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <strong>{t('auto.set.cloudBackupRecommended')}</strong> {t('auto.set.connectGoogleDriveForAutomaticCloud')}</li>
            <li>{t('auto.set.exportABackupBeforeDeployingNew')}</li>
            <li>{t('auto.set.backupsIncludeDatabaseDocumentsReceiptsAvatars')}</li>
            <li>{t('auto.set.testRestoreOnADevelopmentEnvironment')}</li>
            <li>• Backups are compatible across app versions (v2.0+ format)</li>
          </ul>
        </CardContent>
      </Card>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              {t('auto.set.confirmDatabaseRestore')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-3">
                  {t('auto.set.youAreAboutToRestoreThe')}
                  <br />
                  <strong className="text-gray-900">{selectedFile?.name}</strong>
                </p>
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  <strong>{t('auto.set.thisWillReplaceAllExistingData')}</strong>
                  <br />
                  {t('auto.set.allCurrentEmployeesProjectsCustomersTimesheets')}
                </div>
                <p className="mt-3 text-sm">
                  {t('auto.set.makeSureYouHaveExportedA')}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedFile(null)}>{t('auto.set.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-restore-btn"
            >
              {t('auto.set.yesRestoreDatabase')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disconnect Drive Confirmation Dialog */}
      <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <CloudOff className="h-5 w-5" />
              {t('auto.set.disconnectGoogleDrive')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('auto.set.areYouSureYouWantTo')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('auto.set.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnectDrive}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('auto.set.yesDisconnect')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DatabaseBackupTab;
